import type { ConfiguracaoLoja, HorarioFuncionamentoLoja } from '../api'

/** Uma faixa de horário agendável (slot de duração fixa). */
export interface FaixaHorario {
  /** "HH:MM" do início. */
  inicio: string;
  /** "HH:MM" do fim. */
  fim: string;
  /** Rótulo exibido: "20:30 - 21:00". */
  rotulo: string;
  /** Date completo do início, em horário local. Serializado com toISOString(). */
  data: Date;
}

/** Um dia com pelo menos uma faixa agendável. */
export interface DiaAgendavel {
  /** Chave 'YYYY-MM-DD' (valor do select). */
  valor: string;
  /** Rótulo amigável: "Hoje", "Amanhã", "qui, 18/06". */
  rotulo: string;
  faixas: FaixaHorario[];
}

interface Opcoes {
  /** Quantos dias à frente considerar (inclui hoje). */
  dias?: number;
  /** Antecedência mínima em minutos para faixas de hoje. */
  leadMin?: number;
  /** Duração de cada faixa em minutos. */
  slotMin?: number;
}

/** Antecedência mínima padrão do agendamento, em minutos. */
const ANTECEDENCIA_PADRAO_MIN = 30;

/** Antecedência do agendamento: o tempo máximo de preparo da loja, nunca menos que o padrão. */
export function antecedenciaAgendamentoMin(tempoMaxPreparo: number | null | undefined): number {
  return Math.max(ANTECEDENCIA_PADRAO_MIN, tempoMaxPreparo ?? 0);
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function chaveData(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "HH:MM[:SS]" → minutos desde a meia-noite. */
function paraMinutos(hms: string): number {
  const [h, m] = hms.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function hhmm(min: number): string {
  return `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
}

function rotuloData(d: Date, offset: number): string {
  if (offset === 0) return 'Hoje';
  if (offset === 1) return 'Amanhã';
  const fmt = new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
  // "qui., 18/06" → remove o ponto da abreviação do dia da semana.
  return fmt.format(d).replace('.', '');
}

/**
 * Gera os dias agendáveis (e suas faixas) a partir da grade de horários da
 * loja. Considera apenas faixas que cabem inteiras dentro de uma janela ativa;
 * para "hoje", descarta faixas que já passaram (respeitando `leadMin`).
 * Janelas que viram o dia (fim <= início) são ignoradas no MVP.
 */
export function gerarDiasAgendaveis(
  horarios: HorarioFuncionamentoLoja[],
  agora: Date,
  { dias = 14, leadMin = ANTECEDENCIA_PADRAO_MIN, slotMin = 30 }: Opcoes = {},
): DiaAgendavel[] {
  const ativos = horarios.filter((h) => h.ativo);
  const out: DiaAgendavel[] = [];

  for (let i = 0; i < dias; i++) {
    const d = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + i);
    const dow = d.getDay();
    const janelas = ativos.filter((h) => h.diaSemana === dow);
    if (janelas.length === 0) continue;

    const ehHoje = i === 0;
    const limiteMin = ehHoje ? agora.getHours() * 60 + agora.getMinutes() + leadMin : -1;

    const faixas: FaixaHorario[] = [];
    for (const j of janelas) {
      const ini = paraMinutos(j.horaInicio);
      const fim = paraMinutos(j.horaFim);
      for (let s = ini; s + slotMin <= fim; s += slotMin) {
        if (s < limiteMin) continue;
        const data = new Date(d.getFullYear(), d.getMonth(), d.getDate(), Math.floor(s / 60), s % 60);
        faixas.push({ inicio: hhmm(s), fim: hhmm(s + slotMin), rotulo: `${hhmm(s)} - ${hhmm(s + slotMin)}`, data });
      }
    }
    if (faixas.length === 0) continue;
    faixas.sort((a, b) => a.data.getTime() - b.data.getTime());
    out.push({ valor: chaveData(d), rotulo: rotuloData(d, i), faixas });
  }

  return out;
}

interface CondicoesAgendamento {
  modo: ConfiguracaoLoja['agendamentoDelivery']
  lojaFechada: boolean
  temDiaAgendavel: boolean
}

export function agendamentoOfertavel({ modo, lojaFechada, temDiaAgendavel }: CondicoesAgendamento): boolean {
  if (!temDiaAgendavel) return false
  return modo === 'SEMPRE' || (modo === 'SO_FECHADA' && lojaFechada)
}

export interface ProximaAbertura {
  /** 'hoje', 'amanhã', o dia por extenso ('sexta-feira') ou, na semana seguinte, 'na próxima quarta-feira'. */
  rotulo: string
  /** "HH:MM" do início da janela. */
  hora: string
}

const DIAS_SEMANA = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado']

/** "HH:MM[:SS]" → segundos desde a meia-noite (o backend compara com segundos). */
function paraSegundos(hms: string): number {
  const [h, m, s] = hms.split(':').map(Number)
  return ((h ?? 0) * 60 + (m ?? 0)) * 60 + (s ?? 0)
}

function rotuloDia(dia: number, offset: number): string {
  const nome = DIAS_SEMANA[dia]!
  if (offset === 0) return 'hoje'
  if (offset === 1) return 'amanhã'
  if (offset < 7) return nome
  // Mesmo dia da semana de hoje: sem o "próxima", "abre quarta-feira" se lê como hoje.
  return dia === 0 || dia === 6 ? `no próximo ${nome}` : `na próxima ${nome}`
}

/** Dentro de uma janela a grade diz aberta: loja fechada ali é pausa manual, sem hora de abertura a prometer. */
export function proximaAbertura(horarios: HorarioFuncionamentoLoja[], agora: Date): ProximaAbertura | null {
  // Janela que vira o dia nunca abre a loja no backend (início <= agora <= fim).
  const janelas = horarios
    .filter((h) => h.ativo)
    .map((h) => ({ diaSemana: h.diaSemana, inicio: paraSegundos(h.horaInicio), fim: paraSegundos(h.horaFim) }))
    .filter((j) => j.fim > j.inicio)

  const hoje = agora.getDay()
  const agoraSeg = (agora.getHours() * 60 + agora.getMinutes()) * 60 + agora.getSeconds()
  if (janelas.some((j) => j.diaSemana === hoje && j.inicio <= agoraSeg && agoraSeg <= j.fim)) return null

  for (let offset = 0; offset <= 7; offset++) {
    const dia = (hoje + offset) % 7
    const inicios = janelas
      .filter((j) => j.diaSemana === dia && (offset > 0 || j.inicio > agoraSeg))
      .map((j) => j.inicio)
    if (inicios.length === 0) continue
    return { rotulo: rotuloDia(dia, offset), hora: hhmm(Math.floor(Math.min(...inicios) / 60)) }
  }

  return null
}
